import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const PIPELINE = [
  { stage: "AWARDED", items: [{ id: "OB-014", client: "Petrobras Brazil", date: "2026-04-15" }] },
  { stage: "PREQUALIFICATION", items: [{ id: "OB-013", client: "ExxonMobil Suriname", date: "2026-04-08", progress: 72 }] },
  { stage: "CLIENT REVIEW", items: [{ id: "OB-012", client: "TotalEnergies Angola", date: "2026-03-28", progress: 100 }] },
  { stage: "APPROVED", items: [{ id: "OB-011", client: "Azule Energy", date: "2026-03-12" }] },
  { stage: "LIVE", items: [{ id: "OB-010", client: "ExxonMobil Guyana", date: "2026-02-04" },{ id: "OB-009", client: "BP Worldwide", date: "2026-01-20" }] },
];

const QUESTIONS = [
  { id: "Q1", section: "Company", q: "Legal entity name and registration jurisdiction", answer: "Velocity Offshore Holdings LLC, incorporated Republic of the Marshall Islands, March 2024", status: "auto" },
  { id: "Q2", section: "Company", q: "Beneficial owners and shareholding", answer: "Per VOFF UBO declaration — Jerome Arnold (CEO/Founder), Kyle Pemberton (COO), Bo Jardine (CCO). Full UBO doc on file.", status: "auto" },
  { id: "Q3", section: "Insurance", q: "P&I cover details and limits", answer: "Standard Club P&I — $1B limit, current cert valid through 20 Feb 2027. H&M: HDI Global, $30M.", status: "auto" },
  { id: "Q4", section: "HSEQ", q: "ISO 9001 / ISO 14001 / ISO 45001 certification", answer: "ISO 9001:2015 certified Mar 2025 (BV); ISO 45001 certification in progress (target Q3 2026).", status: "auto" },
  { id: "Q5", section: "HSEQ", q: "Last 24 months LTIs/RIDDOR-reportable events", answer: "Zero LTIs since incorporation. One first-aid case (Q4 2025), reported and closed.", status: "auto" },
  { id: "Q6", section: "Vessels", q: "Provide CMID/OVID for each proposed vessel", answer: "⚠ MANUAL — OVID 2026 refresh required. Latest OVID file in Document Controller.", status: "manual" },
  { id: "Q7", section: "Local Content", q: "Local crew percentage by operating region", answer: "⚠ MANUAL — region-dependent. Current Guyana: 28%; Angola: 35%; Nigeria: 42%.", status: "manual" },
  { id: "Q8", section: "Financial", q: "Last 3 years audited financials", answer: "⚠ MANUAL — VOFF incorporated 2024; 2024 audited financials available. 2025 audit in progress.", status: "manual" },
];

const ClientOnboarding = () => {
  return (
    <IntranetLayout>
      <ModuleHeader title="Client Onboarding" subtitle="Vendor Registration & Prequalification Automation" accent="lime"
        stats={[{ label: "Active", value: 4 }, { label: "Completed", value: 11, tone: "good" }]} />

      <Card className="border-[#D0DDE6] mb-6">
        <CardContent className="p-5">
          <div className="grid grid-cols-5 gap-3">
            {PIPELINE.map((p) => (
              <div key={p.stage}>
                <div className="text-[10px] uppercase tracking-widest text-[#6B8A9A] font-bold mb-2">{p.stage}</div>
                <div className="space-y-2">
                  {p.items.map((i) => (
                    <div key={i.id} className="bg-[#F4F7FA] p-3 rounded border border-[#D0DDE6]">
                      <div className="text-xs font-mono text-[#6B8A9A]">{i.id}</div>
                      <div className="text-sm font-bold text-[#00283C]">{i.client}</div>
                      <div className="text-[10px] text-[#6B8A9A] mt-1">{i.date}</div>
                      {(i as any).progress !== undefined && <Progress value={(i as any).progress} className="h-1 mt-1.5" />}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="border-[#D0DDE6]">
          <CardContent className="p-5 space-y-3">
            <h3 className="font-bold text-[#00283C]">New Onboarding</h3>
            <div><Label className="text-xs">Client</Label><Select><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{["ExxonMobil Suriname","Petrobras Brazil","TotalEnergies Angola","Other"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Type</Label><Select defaultValue="Full prequalification"><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Full prequalification","Annual renewal","Vessel addition","OVID/CMID"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs">Contract Description</Label><Input placeholder="2x AIRCAT 35 CTV charter, 12mo + 12mo option" /></div>
            <div><Label className="text-xs">Deadline</Label><Input type="date" /></div>
            <div><Label className="text-xs">Onboarding Portal URL</Label><Input placeholder="https://prequalify.client.com/..." /></div>
            <Button onClick={() => toast.success("Action recorded: Initiate Onboarding")} className="w-full bg-[#8CB43C] hover:bg-[#8CB43C]/90 text-white">Initiate Onboarding</Button>
            <div className="bg-[#F4F7FA] p-3 rounded text-xs text-[#0D1E2B]">
              AI will read the questionnaire and auto-populate from VOFF master data: company, insurance, banking, key personnel, vessel specs, certificates, HSEQ records.
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 border-[#D0DDE6]">
          <CardContent className="p-5">
            <div className="flex justify-between mb-3"><h3 className="font-bold text-[#00283C]">OB-013 — ExxonMobil Suriname (Active)</h3><Badge className="bg-[#288CC8] text-white">72% complete</Badge></div>
            <div className="grid grid-cols-3 gap-2 mb-4 text-xs text-center">
              <div className="bg-[#2CA048]/10 p-2 rounded"><div className="font-bold text-[#2CA048] text-lg">52</div>Auto-populated</div>
              <div className="bg-[#E8A020]/10 p-2 rounded"><div className="font-bold text-[#E8A020] text-lg">8</div>Manual review</div>
              <div className="bg-[#C83228]/10 p-2 rounded"><div className="font-bold text-[#C83228] text-lg">3</div>Missing data</div>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {QUESTIONS.map((q) => (
                <div key={q.id} className="border border-[#D0DDE6] rounded p-3">
                  <div className="flex justify-between items-start mb-1">
                    <div className="text-xs"><span className="font-mono text-[#6B8A9A]">{q.id} · {q.section}</span> — <span className="font-semibold text-[#00283C]">{q.q}</span></div>
                    {q.status === "auto" ? <Badge className="bg-[#2CA048] text-white text-[10px]"><CheckCircle2 className="w-3 h-3 mr-0.5" />AUTO</Badge> : <Badge className="bg-[#E8A020] text-white text-[10px]"><AlertCircle className="w-3 h-3 mr-0.5" />MANUAL</Badge>}
                  </div>
                  <div className="text-xs text-[#0D1E2B] mt-1">{q.answer}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </IntranetLayout>
  );
};
export default ClientOnboarding;
